import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Car, Menu, X, Phone, MessageCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface NavigationProps {
  showBackButton?: boolean;
}

const Navigation: React.FC<NavigationProps> = ({ showBackButton = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isHomePage = location.pathname === '/';

  const scrollToSection = (id: string) => {
    setMobileMenuOpen(false);
    if (!isHomePage) {
      navigate('/', { state: { scrollTo: id } });
    } else {
      const element = document.getElementById(id);
      if (element) {
        const headerOffset = 100;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.scrollY - headerOffset;
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }
  };

  const handleNavigation = (item: any) => {
    if (item.isRoute && item.path) {
      navigate(item.path);
    } else if (item.id === 'contact') {
      navigate('/contact');
    } else {
      scrollToSection(item.id);
    }
  };

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-40 flex items-center justify-between px-6 md:px-8 py-6 bg-gradient-to-b from-[#0f172a]/90 via-[#0f172a]/80 to-transparent backdrop-blur-md">
        <div className="font-heading text-xl md:text-2xl font-bold tracking-tighter text-white cursor-default z-50 flex items-center gap-2">
          <img src="/new_logo.png" alt="Маэстро" className="w-10 h-10 object-contain" />
          <button 
            onClick={() => navigate('/')}
            className="hover:text-[#f97316] transition-colors bg-transparent border-none cursor-pointer"
          >
            МАЭСТРО
          </button>
        </div>
      
      {/* Desktop Menu */}
      <div className="hidden md:flex gap-6 text-base font-bold tracking-wide uppercase items-center">
        {[
          { id: 'courses', label: 'КАТЕГОРИИ' },
          { id: 'about', label: 'О НАС', isRoute: true, path: '/about', submenu: [
            { id: 'about', label: 'О ШКОЛЕ' },
            { path: '/about/fleet', label: 'АВТОПАРК', id: 'fleet' }, 
            { path: '/about/instructors', label: 'ИНСТРУКТОРЫ', id: 'instructors' }
          ]},
          { id: 'locations', label: 'ФИЛИАЛЫ', isRoute: true, path: '/branches' },
          { id: 'reviews', label: 'ОТЗЫВЫ' },
          { id: 'faq', label: 'FAQ' },
          { id: 'pricing', label: 'ЦЕНЫ' },
          { id: 'contact', label: 'КОНТАКТЫ', isRoute: true }
        ].map((item) => (
          <div key={item.id} className="relative group">
            <button 
              onClick={() => handleNavigation(item)}
              className="hover:text-[#f97316] transition-colors text-white cursor-pointer bg-transparent border-none whitespace-nowrap"
              data-hover="true"
            >
              {item.label}
            </button>
            {item.submenu && (
              <div className="absolute top-full left-0 mt-2 bg-[#0f172a] border border-white/10 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 min-w-[160px]">
                {item.submenu.map((sub) => (
                  <button
                    key={sub.path || sub.id}
                    onClick={() => sub.path ? navigate(sub.path) : scrollToSection(sub.id)}
                    className="block w-full text-left px-6 py-3 text-sm hover:bg-[#f97316] hover:text-black transition-colors whitespace-nowrap"
                    data-hover="true"
                  >
                    {sub.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
        <button 
          onClick={() => navigate('/blog')}
          className="hover:text-[#f97316] transition-colors text-white cursor-pointer bg-transparent border-none"
          data-hover="true"
        >
          БЛОГ
        </button>
        <a href="tel:+79191232636" className="text-[#f97316] hover:text-white transition-colors" data-hover="true">
          <Phone className="w-5 h-5" />
        </a>
      </div>
      <button 
        onClick={() => scrollToSection('pricing')}
        className="hidden md:inline-block bg-[#f97316] border-2 border-[#f97316] px-10 py-4 text-lg font-bold tracking-widest uppercase hover:bg-white hover:text-[#f97316] transition-all duration-300 text-black cursor-pointer shadow-lg shadow-[#f97316]/30 rounded-lg"
        data-hover="true"
      >
        ЗАПИСЬ
      </button>

      {/* Mobile Menu Toggle */}
      <button 
        className="md:hidden text-white z-50 relative w-10 h-10 flex items-center justify-center bg-transparent border-none"
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
      >
         {mobileMenuOpen ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
      </button>
      </nav>

      {/* Mobile Menu Overlay - Outside nav for proper z-index */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '100%' }}
            transition={{ type: 'tween', duration: 0.3 }}
            className="fixed inset-0 z-50 bg-[#0f172a]/95 backdrop-blur-2xl flex flex-col items-center justify-start pt-24 gap-6 md:hidden overflow-y-auto"
          >
            {/* Close Button */}
            <button
              onClick={() => setMobileMenuOpen(false)}
              className="absolute top-6 right-6 text-white hover:text-[#f97316] transition-colors bg-transparent border-none p-2"
            >
              <X className="w-8 h-8" />
            </button>

            {[
              { id: 'courses', label: 'КАТЕГОРИИ' },
              { path: '/about', label: 'О НАС', isRoute: true },
              { id: 'about', label: 'О ШКОЛЕ' },
              { path: '/about/fleet', label: 'АВТОПАРК', isRoute: true },
              { path: '/about/instructors', label: 'ИНСТРУКТОРЫ', isRoute: true },
              { id: 'autodrom', label: 'АВТОДРОМ' },
              { path: '/branches', label: 'ФИЛИАЛЫ', isRoute: true },
              { id: 'reviews', label: 'ОТЗЫВЫ' },
              { id: 'faq', label: 'FAQ' },
              { id: 'pricing', label: 'ЦЕНЫ' }
            ].map((item) => (
              <button
                key={item.id || item.path}
                onClick={() => {
                  setMobileMenuOpen(false);
                  if (item.isRoute && item.path) {
                    navigate(item.path);
                  } else if (item.id) {
                    scrollToSection(item.id);
                  }
                }}
                className="text-2xl font-heading font-bold text-white hover:text-[#f97316] transition-colors uppercase bg-transparent border-none"
              >
                {item.label}
              </button>
            ))}
            <button
              onClick={() => { setMobileMenuOpen(false); navigate('/blog'); }}
              className="text-2xl font-heading font-bold text-white hover:text-[#f97316] transition-colors uppercase bg-transparent border-none"
            >
              БЛОГ
            </button>
            <button
              onClick={() => { setMobileMenuOpen(false); navigate('/contact'); }}
              className="text-2xl font-heading font-bold text-white hover:text-[#f97316] transition-colors uppercase bg-transparent border-none"
            >
              КОНТАКТЫ
            </button>
            <div className="flex gap-6 mt-4">
              <a href="tel:+79191232636" className="text-[#f97316] hover:text-white transition-colors">
                <Phone className="w-8 h-8" />
              </a>
            </div>
            <button 
              onClick={() => { setMobileMenuOpen(false); scrollToSection('pricing'); }}
              className="mt-6 mb-8 bg-[#f97316] text-black px-10 py-4 text-sm font-bold tracking-widest uppercase rounded-lg"
            >
              ЗАПИСЬ
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navigation;
